import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dropdown, MenuProps } from 'antd';
import React, { useRef, useState } from 'react';


export interface DraggableDropdownTabProps extends React.HTMLAttributes<HTMLDivElement> {
    'data-node-key': string;
    menuItems: MenuProps['items'];
    children: React.ReactNode;
    type?: string;
    onMenuClick?: (key: string) => void;
}

export const DraggableDropdownTab: React.FC<DraggableDropdownTabProps> = ({ 
    className, 
    menuItems, 
    onMenuClick,
    ...props 
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props['data-node-key'],
    });

    const [mouseDownPos, setMouseDownPos] = useState<{ x: number; y: number } | null>(null);
    const [isRightClick, setIsRightClick] = useState(false);
    const dragThreshold = 5; // 拖拽阈值，超过这个距离才开始拖拽
    const isDraggingRef = useRef(false);

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.6 : 1,
    };

    // 处理鼠标按下事件
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.button === 2) { // 右键
            setIsRightClick(true);
            return;
        }

        if (e.button === 0) { // 左键
            setIsRightClick(false);
            setMouseDownPos({ x: e.clientX, y: e.clientY });
            isDraggingRef.current = false;
        }

        // 调用原始的 onMouseDown
        if (props.onMouseDown) {
            props.onMouseDown(e);
        }
    };

    // 处理鼠标移动事件
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isRightClick || !mouseDownPos) {
            return;
        }

        const deltaX = Math.abs(e.clientX - mouseDownPos.x);
        const deltaY = Math.abs(e.clientY - mouseDownPos.y);
        
        // 当移动距离超过阈值时，开始拖拽
        if ((deltaX > dragThreshold || deltaY > dragThreshold) && !isDraggingRef.current) {
            isDraggingRef.current = true;
        }

        // 调用原始的 onMouseMove
        if (props.onMouseMove) {
            props.onMouseMove(e);
        }
    };

    // 处理鼠标松开事件
    const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.button === 2) { // 右键松开
            setIsRightClick(false);
        }

        if (e.button === 0) { // 左键松开
            setMouseDownPos(null);
            // 延迟重置拖拽状态，确保拖拽操作完成
            setTimeout(() => {
                isDraggingRef.current = false;
            }, 100);
        }

        // 调用原始的 onMouseUp
        if (props.onMouseUp) {
            props.onMouseUp(e);
        }
    };

    // 处理右键菜单
    const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        
        // 如果正在拖拽或刚刚拖拽过，不显示右键菜单
        if (isDraggingRef.current || isDragging) {
            return;
        }

        // 如果不是右键点击，不显示菜单
        if (!isRightClick && mouseDownPos) {
            return;
        }

        // 调用原始的 onContextMenu
        if (props.onContextMenu) {
            props.onContextMenu(e);
        }
    };

    // 处理菜单项点击
    const handleMenuClick = (info: { key: string }) => {
        if (onMenuClick) {
            onMenuClick(info.key);
        }
    };

    // 创建增强的监听器，只在非右键时生效
    const enhancedListeners = {
        ...listeners,
        onMouseDown: (e: any) => {
            if (e.button !== 2 && !isRightClick) {
                listeners?.onMouseDown?.(e);
            }
        },
    };

    return (
        <Dropdown
            menu={{ 
                items: menuItems,
                onClick: handleMenuClick
            }}
            // open={contextMenuOpen}
            // onOpenChange={setContextMenuOpen}
            // trigger={[]} // 不使用默认触发器，我们手动控制
            trigger={['contextMenu']}
            placement="bottomLeft"
            destroyOnHidden
        >
            {React.cloneElement(props.children as React.ReactElement<any>, {
                ref: setNodeRef,
                style,
                ...attributes,
                ...enhancedListeners,
                onMouseDown: handleMouseDown,
                onMouseMove: handleMouseMove,
                onMouseUp: handleMouseUp,
                onContextMenu: handleContextMenu,
                className: `${className || ''} draggable-dropdown-tab`,
            })}
        </Dropdown>
    );
};

export default DraggableDropdownTab;
